/**
 * 상단 크롬의 열린 플랜.
 * 탭은 작업 세트만 기억한다. 플랜을 바꾸면 편집기는 하나만 다시 연다.
 */
import type { ProjectTab } from "@/store/persist";
import { useCatalogStore } from "@/store/catalog-store";
import { planHref } from "@/shell/project-nav";

/** 한 워크스페이스에 올려 두는 탭 수. 넘치면 가장 오래 열어 둔 것부터 내린다. */
export const PLAN_TAB_LIMIT = 12;
/** 플랜 탭의 고정 폭. 글자는 이 안에서 말줄임된다. */
export const PLAN_TAB_WIDTH_PX = 160;
/** 워크스페이스 탭. 좌우 10px, 아이콘 16px, 간격 6px, 「워크스페이스」 6자. */
export const PLAN_TAB_HOME_PX = 124;
export const PLAN_TAB_GAP_PX = 2;
/** 넘친 탭을 담는 `…` 버튼. */
export const PLAN_TAB_MORE_PX = 28;

export function openPlanTab(ids: readonly string[], planId: string, activeId: string | null = null, limit = PLAN_TAB_LIMIT): readonly string[] {
  if (!planId || ids.includes(planId)) return ids;
  const next = [...ids, planId];
  while (next.length > limit) {
    const dropAt = next.findIndex((id) => id !== planId && id !== activeId);
    if (dropAt < 0) break;
    next.splice(dropAt, 1);
  }
  return next;
}

export function closePlanTab(ids: readonly string[], planId: string): string[] {
  return ids.filter((id) => id !== planId);
}

/** 닫은 탭의 오른쪽, 없으면 왼쪽. 닫은 탭이 아니면 null. */
export function planAfterClose(ids: readonly string[], planId: string): string | null {
  const index = ids.indexOf(planId);
  if (index < 0) return null;
  return ids[index + 1] ?? ids[index - 1] ?? null;
}

/** 보이는 칸 안에 현재 플랜이 들어오게 창을 자른다. */
export function splitPlanTabs(ids: readonly string[], activeId: string | null, limit: number): { shown: string[]; overflow: string[] } {
  if (limit <= 0) return { shown: [], overflow: [...ids] };
  if (ids.length <= limit) return { shown: [...ids], overflow: [] };
  let start = Math.max(0, ids.length - limit);
  if (activeId) {
    const index = ids.indexOf(activeId);
    if (index >= 0) {
      if (index < start) start = index;
      if (index >= start + limit) start = index - limit + 1;
    }
  }
  const shown = ids.slice(start, start + limit);
  const shownSet = new Set(shown);
  return { shown, overflow: ids.filter((id) => !shownSet.has(id)) };
}

/**
 * 워크스페이스 탭을 남긴 뒤에 고정 폭 플랜 탭이 몇 개 들어가는지.
 * 다 들어가지 않으면 `…` 칸을 빼고, 나머지는 그 메뉴로 접는다.
 */
export function planTabFitCount(width: number, count: number): number {
  if (count <= 0 || width <= 0) return 0;
  const slot = PLAN_TAB_WIDTH_PX + PLAN_TAB_GAP_PX;
  if (PLAN_TAB_HOME_PX + count * slot <= width) return count;
  const shown = Math.floor((width - PLAN_TAB_HOME_PX - PLAN_TAB_GAP_PX - PLAN_TAB_MORE_PX) / slot);
  return Math.max(0, Math.min(count - 1, shown));
}

export function planTabPath(planId: string, lastTab: ProjectTab | undefined): string {
  return planHref(planId, lastTab ?? "schedule");
}

/** 이 플랜을 크롬에 올린다. 이미 있으면 순서를 바꾸지 않는다. */
export function rememberPlanTab(planId: string) {
  const catalog = useCatalogStore.getState();
  if (!catalog.projects[planId]) return;
  const workspace = catalog.workspaces[catalog.activeWorkspaceId];
  if (!workspace) return;
  const stored = (workspace.openPlanIds ?? []).filter((id) => catalog.projects[id]);
  const next = openPlanTab(stored, planId, catalog.activeProjectId);
  if (next === stored) return;
  catalog.setOpenPlanIds([...next]);
}

/** 탭을 내린다. 그 자리에 남길 이웃 플랜이 있으면 그 id를 돌려준다. */
export function dismissPlanTab(planId: string): string | null {
  const catalog = useCatalogStore.getState();
  const workspace = catalog.workspaces[catalog.activeWorkspaceId];
  const stored = (workspace?.openPlanIds ?? []).filter((id) => catalog.projects[id]);
  const neighbor = planAfterClose(stored, planId);
  catalog.setOpenPlanIds(closePlanTab(stored, planId));
  return neighbor;
}
